import EditIcon from "@mui/icons-material/Edit"
import { Avatar, Box, IconButton } from "@mui/material"
import React, { ChangeEvent, useEffect, useRef, useState } from "react"
import { detectImageType } from "../../utils/checkImageType"

interface EditableAvatarProps {
	currentImage: File | undefined
	size?: number
	onChange: (file: File) => void
}

const EditableAvatar: React.FC<EditableAvatarProps> = ({
	currentImage,
	size = 120,
	onChange,
}) => {
	const [image, setImage] = useState<string | null>(null)
	const fileInputRef = useRef<HTMLInputElement>(null)

	const getImageSrc = (raw: string | undefined | null) => {
		if (!raw) return undefined

		// Se contiene già "data:image", è una data URL valida
		if (raw.startsWith("data:image")) {
			return raw
		}

		// Prova a capire che tipo di immagine è
		const firstChars = raw.slice(0, 20)

		if (firstChars.includes("<svg")) {
			// SVG XML inline (non base64), bisogna convertirlo in data URI
			return `data:image/svg+xml;utf8,${encodeURIComponent(raw)}`
		}

		const base64Pure = btoa(raw)
		const imageType = detectImageType(base64Pure)
		// Altrimenti assumiamo sia una stringa base64 pura → aggiungiamo intestazione generica PNG (o deduci meglio se serve)
		return `data:${imageType};base64,${base64Pure}`
	}

	const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
		const { name, files } = event.target
		const currentFile = files?.[0]
		if (currentFile) {
			onChange(currentFile)
			const reader = new FileReader()
			reader.onloadend = () => {
				setImage(reader.result)
				/*setFormData((prevData) => ({
						...prevData,
						[name]: btoa(reader.result),
					}))*/
			}
			reader.readAsDataURL(currentFile)
		}
	}

	const handleEditClick = () => {
		fileInputRef.current?.click()
	}

	useEffect(() => {
		const base64Image = currentImage ?? undefined
		const userAvatarSrc = base64Image && atob(base64Image)
		const imageSrcToRender = getImageSrc(userAvatarSrc)
		setImage(imageSrcToRender)
	}, [currentImage])

	return (
		<Box
			sx={{
				position: "relative",
				width: 150,
				height: 150,
				backgroundColor: "#f9f9f9",
				border: "3px solid #bbb",
				borderRadius: "50%",
			}}
		>
			<Box
				className="selected-image-holder"
				sx={{
					width: "100%",
					height: "100%",
					borderRadius: "50%",
					overflow: "hidden",
				}}
			>
				<Avatar
					src={image || ""}
					sx={{
						width: "100%",
						height: "100%",
						objectFit: "contain",
						objectPosition: "center center",
						display: "flex",
					}}
				/>
			</Box>
			<IconButton
				size="small"
				sx={{
					position: "absolute",
					width: "40px",
					height: "40px",
					bottom: "-10px",
					right: "5px",
					bgcolor: "rgba(245, 245, 245, 0.62)",
					borderRadius: "50%",
					padding: "5px 7px",
					cursor: "pointer",
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					zIndex: 2,
					border: "1px solid #ccc",
					"&:hover": {
						backgroundColor: "#f0f0f0",
					},
				}}
				onClick={handleEditClick}
			>
				<EditIcon
					fontSize="small"
					sx={{ width: "100%", height: "100%", objectFit: "scale-down" }}
				/>
			</IconButton>
			<input
				type="file"
				accept="image/*"
				style={{ display: "none" }}
				ref={fileInputRef}
				onChange={handleFileChange}
			/>
		</Box>
	)
}

export default EditableAvatar
